import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import {
  EMPTY_REVIEW_FORM,
  REVIEW_CATEGORIES,
  ReviewPoster,
  ScoreStar,
  normaliseFavoriteRanks,
  normaliseReviewCategories,
} from './ReviewShared';

const TOKEN_STORAGE_KEY = 'eliteari-reviews-admin-token';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function toFormEntry(entry) {
  if (!entry) return { ...EMPTY_REVIEW_FORM, categories: ['Anime'], favoriteRanks: {} };
  const categories = normaliseReviewCategories(entry);
  const favoriteRanks = normaliseFavoriteRanks(entry, categories);
  const videoLinks = Array.isArray(entry.videoLinks) && entry.videoLinks.length > 0
    ? entry.videoLinks.map((link) => ({ url: link.url || '', label: link.label || '' }))
    : [{ url: entry.videoUrl || '', label: '' }];
  return {
    ...EMPTY_REVIEW_FORM,
    ...entry,
    categories,
    category: categories[0],
    favoriteRanks,
    favoriteRank: favoriteRanks[categories[0]] ?? '',
    alternateTitles: Array.isArray(entry.alternateTitles) ? entry.alternateTitles.join('\n') : '',
    videoLinks,
  };
}

function searchableTitles(entry) {
  const alternates = Array.isArray(entry.alternateTitles)
    ? entry.alternateTitles
    : String(entry.alternateTitles || '').split('\n');
  return [entry.title, ...alternates]
    .map((title) => String(title || '').trim().toLocaleLowerCase())
    .filter(Boolean);
}

function ReviewManager({ baseUrl }) {
  const apiBase = baseUrl.replace(/\/$/, '');
  const [savedToken, setSavedToken] = useState(() => sessionStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(Boolean(savedToken));
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(() => toFormEntry(null));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [message, setMessage] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [posterSearchOpen, setPosterSearchOpen] = useState(false);
  const [posterSource, setPosterSource] = useState('tmdb');
  const [posterQuery, setPosterQuery] = useState('');
  const [posterResults, setPosterResults] = useState([]);
  const [posterSearching, setPosterSearching] = useState(false);
  const [posterSearchMessage, setPosterSearchMessage] = useState('');

  const loadEntries = React.useCallback(async () => {
    setLoadingEntries(true);
    try {
      const response = await axios.get(`${apiBase}/reviews`);
      setEntries(response.data?.entries || []);
    } catch (error) {
      setMessage({ severity: 'error', text: 'Could not load the local reviews file.' });
    } finally {
      setLoadingEntries(false);
    }
  }, [apiBase]);

  const authenticate = React.useCallback(async (candidate) => {
    setCheckingAuth(true);
    setMessage(null);
    try {
      await axios.post(`${apiBase}/reviews/admin/auth`, {}, { headers: authHeaders(candidate) });
      sessionStorage.setItem(TOKEN_STORAGE_KEY, candidate);
      setSavedToken(candidate);
      setAuthenticated(true);
      setTokenInput('');
      return true;
    } catch (error) {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      setSavedToken('');
      setAuthenticated(false);
      setMessage({
        severity: 'error',
        text: error?.response?.data?.message || 'Could not authenticate with the local server.',
      });
      return false;
    } finally {
      setCheckingAuth(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (savedToken && !authenticated) authenticate(savedToken);
  }, [authenticate, authenticated, savedToken]);

  useEffect(() => {
    if (authenticated) loadEntries();
  }, [authenticated, loadEntries]);

  const sortedEntries = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return entries
      .filter((entry) => (
        !query
        || entry.title.toLocaleLowerCase().includes(query)
        || searchableTitles(entry).some((title) => title.includes(query))
        || (entry.review || '').toLocaleLowerCase().includes(query)
      ))
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [entries, search]);

  const possibleDuplicates = useMemo(() => {
    const candidateTitles = new Set(searchableTitles(form));
    return entries.filter((entry) => {
      if (entry.id === form.id) return false;
      const sameSource = Boolean(
        form.source
        && form.sourceId
        && entry.source === form.source
        && String(entry.sourceId || '') === String(form.sourceId)
      );
      const sameTitle = searchableTitles(entry).some((title) => candidateTitles.has(title));
      return sameSource || sameTitle;
    });
  }, [entries, form]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const toggleCategory = (category) => {
    setForm((current) => {
      const selected = normaliseReviewCategories(current);
      if (selected.includes(category) && selected.length === 1) return current;
      const categories = selected.includes(category)
        ? selected.filter((item) => item !== category)
        : [...selected, category];
      const favoriteRanks = { ...normaliseFavoriteRanks(current, selected) };
      Object.keys(favoriteRanks).forEach((rankCategory) => {
        if (!categories.includes(rankCategory)) delete favoriteRanks[rankCategory];
      });
      return {
        ...current,
        categories,
        category: categories[0],
        favoriteRanks,
        favoriteRank: favoriteRanks[categories[0]] ?? '',
      };
    });
  };

  const updateFavoriteRank = (category) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const favoriteRanks = { ...normaliseFavoriteRanks(current) };
      if (value === '') delete favoriteRanks[category];
      else favoriteRanks[category] = Number(value);
      return {
        ...current,
        favoriteRanks,
        favoriteRank: favoriteRanks[current.categories[0]] ?? '',
      };
    });
  };

  const updateManualImageUrl = (event) => {
    setForm((current) => ({
      ...current,
      imageUrl: event.target.value,
      source: '',
      sourceId: '',
      sourceUrl: '',
    }));
  };

  const updateVideoLink = (index, field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({
      ...current,
      videoLinks: current.videoLinks.map((link, linkIndex) => (
        linkIndex === index ? { ...link, [field]: value } : link
      )),
    }));
  };

  const addVideoLink = () => {
    setForm((current) => ({
      ...current,
      videoLinks: [...current.videoLinks, { url: '', label: '' }],
    }));
  };

  const removeVideoLink = (index) => {
    setForm((current) => ({
      ...current,
      videoLinks: current.videoLinks.length === 1
        ? [{ url: '', label: '' }]
        : current.videoLinks.filter((_, linkIndex) => linkIndex !== index),
    }));
  };

  const openPosterSearch = (source) => {
    setPosterSource(source);
    setPosterQuery(form.title || '');
    setPosterResults([]);
    setPosterSearchMessage('');
    setPosterSearchOpen(true);
  };

  const searchPosters = async (event) => {
    event.preventDefault();
    const query = posterQuery.trim();
    if (query.length < 2) {
      setPosterSearchMessage('Enter at least 2 characters.');
      return;
    }

    setPosterSearching(true);
    setPosterResults([]);
    setPosterSearchMessage('');
    try {
      const response = await axios.get(`${apiBase}/reviews/admin/posters`, {
        params: { source: posterSource, query },
        headers: authHeaders(savedToken),
      });
      const results = response.data?.results || [];
      setPosterResults(results);
      if (results.length === 0) setPosterSearchMessage('No posters found. Try another title.');
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 503) {
        setAuthenticated(false);
        setPosterSearchOpen(false);
      }
      setPosterSearchMessage(error?.response?.data?.message || 'Could not search for posters.');
    } finally {
      setPosterSearching(false);
    }
  };

  const choosePoster = (poster) => {
    setForm((current) => ({
      ...current,
      imageUrl: poster.imageUrl,
      source: poster.source || '',
      sourceId: poster.sourceId || '',
      sourceUrl: poster.sourceUrl || '',
    }));
    setPosterSearchOpen(false);
    setMessage({ severity: 'success', text: `Poster selected from ${poster.sourceLabel || poster.source}. Save the review to keep it.` });
  };

  const downloadBackup = async () => {
    setDownloading(true);
    setMessage(null);
    try {
      const response = await axios.get(`${apiBase}/reviews/admin/export`, {
        headers: authHeaders(savedToken),
        responseType: 'blob',
      });
      const downloadUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reviews-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      setMessage({ severity: 'success', text: 'Reviews backup downloaded.' });
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 503) setAuthenticated(false);
      setMessage({ severity: 'error', text: error?.response?.data?.message || 'Could not download the reviews backup.' });
    } finally {
      setDownloading(false);
    }
  };

  const startNew = () => {
    setForm(toFormEntry(null));
    setMessage(null);
  };

  const startEdit = (entry) => {
    setForm(toFormEntry(entry));
    setMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const duplicateEntry = () => {
    setForm((current) => ({
      ...current,
      id: null,
      title: current.title ? `${current.title} (Copy)` : '',
      favoriteRank: '',
      favoriteRanks: {},
    }));
    setMessage({ severity: 'info', text: 'Copy ready. Make any changes, then save it as a new entry.' });
  };

  const saveEntry = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    const editing = Boolean(form.id);
    const videoLinks = form.videoLinks
      .map((link) => ({ url: link.url.trim(), label: link.label.trim() }))
      .filter((link) => link.url || link.label);
    const categories = normaliseReviewCategories(form);
    const favoriteRanks = normaliseFavoriteRanks(form, categories);
    const payload = {
      ...form,
      categories,
      category: categories[0],
      score: Number(form.score),
      favoriteRanks,
      favoriteRank: favoriteRanks[categories[0]] ?? null,
      alternateTitles: String(form.alternateTitles || '')
        .split('\n')
        .map((title) => title.trim())
        .filter(Boolean),
      videoLinks,
      videoUrl: videoLinks[0]?.url || '',
    };

    try {
      const response = editing
        ? await axios.put(`${apiBase}/reviews/admin/entries/${form.id}`, payload, { headers: authHeaders(savedToken) })
        : await axios.post(`${apiBase}/reviews/admin/entries`, payload, { headers: authHeaders(savedToken) });
      setEntries(response.data?.entries || []);
      setForm(toFormEntry(response.data?.entry));
      setMessage({ severity: 'success', text: editing ? 'Review updated.' : 'Review added.' });
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 503) {
        setAuthenticated(false);
      }
      setMessage({
        severity: 'error',
        text: error?.response?.data?.message || 'Could not save this review.',
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteEntry) return;
    setSaving(true);
    try {
      const response = await axios.delete(
        `${apiBase}/reviews/admin/entries/${deleteEntry.id}`,
        { headers: authHeaders(savedToken) }
      );
      setEntries(response.data?.entries || []);
      if (form.id === deleteEntry.id) startNew();
      setDeleteEntry(null);
      setMessage({ severity: 'success', text: 'Review deleted.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error?.response?.data?.message || 'Could not delete this review.' });
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    setSavedToken('');
    setAuthenticated(false);
    setEntries([]);
    setForm(toFormEntry(null));
    setMessage(null);
  };

  if (!authenticated) {
    return (
      <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: '#f7f7f8', py: { xs: 4, sm: 8 } }}>
        <Container maxWidth="xs">
          <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: '1px solid #e4e4e7', textAlign: 'center' }}>
            <Box sx={{ width: 58, height: 58, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: '#18181b', color: '#fff' }}>
              <LockOutlinedIcon />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Review management</Typography>
            <Typography variant="body2" sx={{ mt: 1, mb: 2.5, color: '#71717a' }}>
              Enter your admin token to add or edit reviews.
            </Typography>
            {message ? <Alert severity={message.severity} sx={{ mb: 2, textAlign: 'left' }}>{message.text}</Alert> : null}
            <Box component="form" onSubmit={(event) => { event.preventDefault(); authenticate(tokenInput); }}>
              <TextField
                fullWidth
                type="password"
                label="Admin token"
                autoComplete="current-password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
              />
              {window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#71717a' }}>
                  Local draft token: <strong>local-reviews-admin</strong>
                </Typography>
              ) : null}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={!tokenInput || checkingAuth}
                sx={{ mt: 2.5, py: 1.1, borderRadius: 999, backgroundColor: '#18181b', fontWeight: 850 }}
              >
                {checkingAuth ? <CircularProgress size={20} color="inherit" /> : 'Unlock editor'}
              </Button>
            </Box>
            <Button component={RouterLink} to="/reviews" startIcon={<ArrowBackIcon />} sx={{ mt: 1.5, textTransform: 'none' }}>
              Back to reviews
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundColor: '#f7f7f8', py: { xs: 2, sm: 3.5 } }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: '-0.035em' }}>Manage reviews</Typography>
            <Typography variant="body2" sx={{ color: '#71717a' }}>Add, edit, rank, duplicate, and remove your entries.</Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button onClick={downloadBackup} disabled={downloading} variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 999, textTransform: 'none' }}>
              {downloading ? 'Downloading...' : 'Backup JSON'}
            </Button>
            <Button component={RouterLink} to="/reviews" variant="outlined" startIcon={<ArrowBackIcon />} sx={{ borderRadius: 999, textTransform: 'none' }}>View page</Button>
            <Button onClick={logout} startIcon={<LogoutIcon />} sx={{ borderRadius: 999, textTransform: 'none', color: '#52525b' }}>Lock</Button>
          </Stack>
        </Stack>

        {message ? <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2, borderRadius: 3 }}>{message.text}</Alert> : null}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e4e4e7', overflow: 'hidden' }}>
            <Box sx={{ p: 1.5 }}>
              <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={startNew} sx={{ borderRadius: 999, backgroundColor: '#18181b', fontWeight: 850, textTransform: 'none' }}>
                New review
              </Button>
              <TextField
                fullWidth
                size="small"
                placeholder="Find an entry..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ mt: 1.25, '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
              />
            </Box>
            <Divider />
            <Box sx={{ maxHeight: { xs: 350, md: 'calc(100vh - 260px)' }, overflowY: 'auto' }}>
              {loadingEntries ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
              ) : sortedEntries.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, color: '#71717a', textAlign: 'center' }}>No matching entries.</Typography>
              ) : sortedEntries.map((entry) => (
                <Box
                  key={entry.id}
                  onClick={() => startEdit(entry)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    p: 1.2,
                    cursor: 'pointer',
                    borderBottom: '1px solid #f1f1f2',
                    backgroundColor: form.id === entry.id ? '#fffbeb' : '#fff',
                    '&:hover': { backgroundColor: form.id === entry.id ? '#fffbeb' : '#fafafa' },
                  }}
                >
                  <ReviewPoster entry={entry} compact />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.title}</Typography>
                    <Typography variant="caption" sx={{ color: '#71717a' }}>
                      {normaliseReviewCategories(entry).join(' · ')} · {entry.score}/5
                    </Typography>
                    {Object.entries(normaliseFavoriteRanks(entry)).map(([category, rank]) => (
                      <Chip
                        key={category}
                        size="small"
                        label={`${category} #${rank}`}
                        sx={{ mt: 0.4, mr: 0.4, height: 20, backgroundColor: '#facc15', fontWeight: 900 }}
                      />
                    ))}
                  </Box>
                  <EditIcon sx={{ color: '#a1a1aa', fontSize: 18 }} />
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper component="form" onSubmit={saveEntry} elevation={0} sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: '1px solid #e4e4e7' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>{form.id ? 'Edit review' : 'Add a review'}</Typography>
              {form.id ? (
                <Stack direction="row" spacing={0.5}>
                  <IconButton onClick={duplicateEntry} title="Duplicate entry" aria-label="Duplicate entry"><ContentCopyIcon /></IconButton>
                  <IconButton onClick={() => setDeleteEntry(form)} title="Delete entry" aria-label="Delete entry" sx={{ color: '#dc2626' }}><DeleteOutlineIcon /></IconButton>
                </Stack>
              ) : null}
            </Stack>

            <TextField fullWidth label="Title" required value={form.title} onChange={updateField('title')} />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.8, fontWeight: 850 }}>Categories</Typography>
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
              {REVIEW_CATEGORIES.map((category) => {
                const selected = normaliseReviewCategories(form).includes(category);
                return (
                  <Button
                    key={category}
                    type="button"
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => toggleCategory(category)}
                    aria-pressed={selected}
                    sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 850 }}
                  >
                    {category}
                  </Button>
                );
              })}
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: '#71717a' }}>
              Select every category where this entry should appear. At least one is required.
            </Typography>

            {possibleDuplicates.length > 0 ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Possible duplicate: {possibleDuplicates.slice(0, 3).map((entry) => entry.title).join(', ')}.
                {' '}You can still save this entry if it is intentional.
              </Alert>
            ) : null}

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Alternative titles"
              value={form.alternateTitles}
              onChange={updateField('alternateTitles')}
              helperText="Optional. Enter one title per line. These stay hidden and improve searching and duplicate detection."
              sx={{ mt: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.8, fontWeight: 850 }}>Score</Typography>
            <Stack direction="row" spacing={{ xs: 0.3, sm: 0.8 }} useFlexGap flexWrap="wrap">
              {[1, 2, 3, 4, 5].map((score) => (
                <Button
                  key={score}
                  type="button"
                  aria-label={`Set score to ${score} out of 5`}
                  onClick={() => setForm((current) => ({ ...current, score }))}
                  sx={{
                    minWidth: 54,
                    p: 0.2,
                    borderRadius: 2,
                    border: Number(form.score) === score ? '2px solid #18181b' : '2px solid transparent',
                    backgroundColor: Number(form.score) === score ? '#f4f4f5' : 'transparent',
                  }}
                >
                  <ScoreStar score={score} compact />
                </Button>
              ))}
            </Stack>

            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Date consumed/completed"
                value={form.completedDate}
                onChange={updateField('completedDate')}
                placeholder="YYYY or MM/YYYY"
                helperText="Optional. For example: 2024 or 08/2024."
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.8, fontWeight: 850 }}>Top 5 rankings</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {normaliseReviewCategories(form).map((category) => (
                <TextField
                  key={category}
                  select
                  fullWidth
                  label={`${category} rank`}
                  value={normaliseFavoriteRanks(form)[category] ?? ''}
                  onChange={updateFavoriteRank(category)}
                  helperText="Choosing an occupied rank swaps the entries."
                >
                  <MenuItem value="">Not in the Top 5</MenuItem>
                  {[1, 2, 3, 4, 5].map((rank) => <MenuItem key={rank} value={rank}>#{rank}</MenuItem>)}
                </TextField>
              ))}
            </Box>

            <TextField
              fullWidth
              label="Image URL"
              value={form.imageUrl}
              onChange={updateManualImageUrl}
              placeholder="https://..."
              helperText="Paste any complete http or https image URL."
              sx={{ mt: 2 }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ImageSearchIcon />}
                onClick={() => openPosterSearch('tmdb')}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Search TMDB
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ImageSearchIcon />}
                onClick={() => openPosterSearch('anime')}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Search Anime APIs
              </Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<ImageSearchIcon />}
                onClick={() => openPosterSearch('manga')}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Search Manga APIs
              </Button>
            </Stack>
            {form.source ? (
              <Typography variant="caption" sx={{ mt: 0.8, display: 'block', color: '#71717a' }}>
                Poster source: {form.source === 'tmdb' ? 'TMDB' : form.source === 'myanimelist' ? 'MyAnimeList' : form.source}
                {form.sourceId ? ` · ID ${form.sourceId}` : ''}
                {form.sourceUrl ? (
                  <Box component="a" href={form.sourceUrl} target="_blank" rel="noopener noreferrer" sx={{ ml: 0.7, color: '#2563eb' }}>
                    View source
                  </Box>
                ) : null}
              </Typography>
            ) : null}

            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Written review"
              value={form.review}
              onChange={updateField('review')}
              helperText="Optional. You can add a written review, a video URL, both, or neither."
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>Video reviews</Typography>
                <Button
                  type="button"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addVideoLink}
                  disabled={form.videoLinks.length >= 10}
                  sx={{ textTransform: 'none' }}
                >
                  Add another link
                </Button>
              </Stack>
              <Stack spacing={1.2}>
                {form.videoLinks.map((link, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1.4fr) minmax(0, 1fr) auto' },
                      gridTemplateAreas: { xs: '"url remove" "label label"', sm: '"url label remove"' },
                      gap: 1,
                      alignItems: 'start',
                    }}
                  >
                    <TextField
                      fullWidth
                      label={`Video URL ${index + 1}`}
                      value={link.url}
                      onChange={updateVideoLink(index, 'url')}
                      placeholder="https://www.youtube.com/watch?v=..."
                      sx={{ gridArea: 'url' }}
                    />
                    <TextField
                      fullWidth
                      label="Button text"
                      value={link.label}
                      onChange={updateVideoLink(index, 'label')}
                      placeholder={form.videoLinks.length > 1 ? `Watch review ${index + 1}` : 'Watch my review'}
                      helperText="Optional custom text"
                      sx={{ gridArea: 'label' }}
                    />
                    <IconButton
                      type="button"
                      onClick={() => removeVideoLink(index)}
                      aria-label={`Remove video link ${index + 1}`}
                      title="Remove video link"
                      sx={{ gridArea: 'remove', color: '#dc2626' }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2.5, backgroundColor: '#fafafa', border: '1px solid #e4e4e7' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#71717a', fontWeight: 850 }}>QUICK PREVIEW</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ReviewPoster entry={{ ...form, title: form.title || 'Untitled review' }} compact />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>{form.title || 'Untitled review'}</Typography>
                  <Typography variant="body2" sx={{ color: '#71717a' }}>
                    {normaliseReviewCategories(form).join(' · ')}
                  </Typography>
                </Box>
                <ScoreStar score={form.score} compact />
              </Stack>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              startIcon={saving ? null : <SaveIcon />}
              disabled={saving}
              sx={{ mt: 2.5, py: 1.15, borderRadius: 999, backgroundColor: '#18181b', fontWeight: 900, textTransform: 'none' }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : form.id ? 'Save changes' : 'Add review'}
            </Button>
          </Paper>
        </Box>
      </Container>

      <Dialog open={Boolean(deleteEntry)} onClose={() => setDeleteEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete this review?</DialogTitle>
        <DialogContent>
          <Typography>This permanently removes <strong>{deleteEntry?.title}</strong> from the reviews file.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteEntry(null)} disabled={saving}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={saving} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={posterSearchOpen} onClose={() => setPosterSearchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Find a poster</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              type="button"
              variant={posterSource === 'tmdb' ? 'contained' : 'outlined'}
              onClick={() => { setPosterSource('tmdb'); setPosterResults([]); setPosterSearchMessage(''); }}
              sx={{ borderRadius: 999, textTransform: 'none', ...(posterSource === 'tmdb' ? { backgroundColor: '#18181b' } : {}) }}
            >
              TMDB
            </Button>
            <Button
              type="button"
              variant={posterSource === 'anime' ? 'contained' : 'outlined'}
              onClick={() => { setPosterSource('anime'); setPosterResults([]); setPosterSearchMessage(''); }}
              sx={{ borderRadius: 999, textTransform: 'none', ...(posterSource === 'anime' ? { backgroundColor: '#18181b' } : {}) }}
            >
              Anime APIs
            </Button>
            <Button
              type="button"
              variant={posterSource === 'manga' ? 'contained' : 'outlined'}
              onClick={() => { setPosterSource('manga'); setPosterResults([]); setPosterSearchMessage(''); }}
              sx={{ borderRadius: 999, textTransform: 'none', ...(posterSource === 'manga' ? { backgroundColor: '#18181b' } : {}) }}
            >
              Manga APIs
            </Button>
          </Stack>

          <Box component="form" onSubmit={searchPosters} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              autoFocus
              size="small"
              label="Title"
              value={posterQuery}
              onChange={(event) => setPosterQuery(event.target.value)}
              placeholder={posterSource === 'tmdb' ? 'Movie or TV show title' : posterSource === 'manga' ? 'Manga title' : 'Anime title'}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={posterSearching}
              sx={{ minWidth: 100, backgroundColor: '#18181b', textTransform: 'none' }}
            >
              {posterSearching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
          </Box>

          {posterSearchMessage ? <Alert severity="info" sx={{ mt: 2 }}>{posterSearchMessage}</Alert> : null}

          {posterResults.length > 0 ? (
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {posterResults.map((poster) => (
                <Paper
                  key={poster.id}
                  component="button"
                  type="button"
                  onClick={() => choosePoster(poster)}
                  elevation={0}
                  sx={{
                    p: 0,
                    overflow: 'hidden',
                    borderRadius: 2.5,
                    border: '1px solid #e4e4e7',
                    backgroundColor: '#fff',
                    color: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { borderColor: '#f59e0b', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' },
                  }}
                >
                  <Box
                    component="img"
                    src={poster.imageUrl}
                    alt={`Poster for ${poster.title}`}
                    loading="lazy"
                    sx={{ width: '100%', aspectRatio: '2 / 3', objectFit: 'cover', display: 'block', backgroundColor: '#e4e4e7' }}
                  />
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 850, lineHeight: 1.2 }}>{poster.title}</Typography>
                    <Typography variant="caption" sx={{ color: '#71717a' }}>
                      {[poster.mediaType, poster.year].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPosterSearchOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReviewManager;
